import { action as mobxAction, makeObservable, observable } from 'mobx';
import { ERROR_CODES } from '../utils/errorHandler';
import type { CaptchaStatus } from '../utils/constants';
import { CONDUCTOR_FT, EXPERIMENTS } from './constants';
import { RootStore } from './root';

export const CAPTCHA_ACTIONS = {
  GET_USER_ACCOUNTS: 'GET_USER_ACCOUNTS',
  LOGIN: 'LOGIN',
  SIGNUP: 'SIGNUP',
  FORGOT: 'FORGOT',
};

export class CaptchaStore {
  private readonly rootStore: RootStore;
  readonly RECAPTCH_ENTERPRISE_ELEMENT_ID = 'recaptcha-widget';
  readonly showCaptchaOnGteUserAccounts: boolean;
  // When a visible captcha is required but unsolved (server codes 9981/9982),
  // the Recaptcha component shows a prompt under the widget telling the user to
  // tick the "I'm not a robot" checkbox. Observable so that component re-renders.
  showCheckboxValidationMessage = false;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    this.showCaptchaOnGteUserAccounts = this.rootStore.experiments.enabled(
      'specs.ident.ExecuteCaptchaOnGetUserAccountsByEmail',
    );
    makeObservable(this, {
      showCheckboxValidationMessage: observable,
      setShowCheckboxValidationMessage: mobxAction,
    });
  }

  setShowCheckboxValidationMessage(show: boolean) {
    this.showCheckboxValidationMessage = show;
  }

  // Conductor FT (users scope) gating the under-captcha prompt; off → behaves as
  // before (captcha renders, no prompt). Read at point of use so it stays mockable.
  private get isCheckboxPromptEnabled() {
    return (
      this.rootStore.experiments.get(
        EXPERIMENTS.SHOW_CAPTCHA_CHECKBOX_PROMPT,
      ) === CONDUCTOR_FT.ENABLED
    );
  }

  get invisibleCaptchaKey() {
    return window.__RECAPTCHA_INVISIBLE_KEY__;
  }

  get enterpriseCaptchaKey() {
    return window.__RECAPTCHA_ENTERPRISE_KEY__;
  }

  get captcha() {
    return window.grecaptcha?.enterprise;
  }

  createOrResetCaptchaIfNeeded(
    serverError: string | number,
    captchaAdded: boolean,
    onSolved?: () => void,
  ): boolean {
    const isCaptchaError = this.isCaptchaServerError(serverError);
    // The captcha itself always renders on a captcha error; only the prompt is
    // FT-gated.
    const showPrompt = isCaptchaError && this.isCheckboxPromptEnabled;
    if (captchaAdded) {
      this.reload();
      // The widget is already up; keep the prompt only while the latest failure
      // is still the unsolved captcha, drop it once the user got past it.
      this.setShowCheckboxValidationMessage(showPrompt);
      return true;
    } else if (serverError && isCaptchaError) {
      this.renderReCaptchaEnterprise({ callback: onSolved });
      this.setShowCheckboxValidationMessage(showPrompt);
      return true;
    } else {
      return false;
    }
  }

  /**
   * Shows the CAPTCHA a refused request asked for, once its form is on screen.
   * A widget from an earlier refusal may still be mounted; rendering into that
   * element again throws, so it is reset instead. Returns whether one is shown.
   */
  renderForRefusedRequest(errorCode: string, onSolved?: () => void): boolean {
    const widgetElement =
      typeof document === 'undefined'
        ? null
        : document.getElementById(this.RECAPTCH_ENTERPRISE_ELEMENT_ID);
    return this.createOrResetCaptchaIfNeeded(
      errorCode,
      Boolean(widgetElement?.childElementCount),
      onSolved,
    );
  }

  async handleRecaptchaExecution({
    captchaAdded,
    action,
  }: {
    captchaAdded: boolean;
    action: string;
  }) {
    if (
      !this.showCaptchaOnGteUserAccounts &&
      action === CAPTCHA_ACTIONS.GET_USER_ACCOUNTS
    ) {
      return;
    }
    const invisibleRecaptcha = await this.executeInvisibleCaptcha(action).catch(
      () => undefined,
    );
    const recaptchaParams = captchaAdded ? this.recaptchaParams() : undefined;

    return { invisibleRecaptcha, ...recaptchaParams };
  }

  recaptchaParams(): RecaptchaParams {
    return {
      'recaptchaParams.recaptcha_response_field': this.captcha?.getResponse(),
      'recaptchaParams.recaptchaServerType': 'GOOGLE_ENTERPRISE',
    };
  }

  reload() {
    this.captcha?.reset();
  }

  executeInvisibleCaptcha(action: string) {
    return this.captcha?.execute(this.invisibleCaptchaKey, { action });
  }

  isCaptchaServerError = (serverError: string | number) =>
    String(serverError) === ERROR_CODES.CAPTCHA_FAILED ||
    String(serverError) === ERROR_CODES.CAPTCHA_REQUIRED;

  renderReCaptchaEnterprise = ({
    elementId = this.RECAPTCH_ENTERPRISE_ELEMENT_ID,
    theme = 'light',
    callback,
  }: {
    elementId?: string;
    theme?: string;
    /** Called once the user solves the widget. */
    callback?: () => void;
  }) =>
    this.captcha?.render(elementId, {
      sitekey: this.enterpriseCaptchaKey,
      theme,
      ...(callback && { callback }),
    });
}

/**
 * A CAPTCHA that a refused request asked for while its form was not on screen
 * (a code send from the code screen). The form renders it once mounted, and
 * again when a request from the form itself is refused. `context` names the
 * request whose next attempt sends the solved token; `onSolved` retries it as
 * soon as the user solves the widget.
 */
export class PendingCaptcha {
  errorCode = '';
  private context?: keyof CaptchaStatus;
  private onSolved?: () => void;

  constructor(
    private readonly getCaptchaStore: () => CaptchaStore,
    private readonly onRendered: (
      shown: boolean,
      context?: keyof CaptchaStatus,
    ) => void,
  ) {
    makeObservable(this, {
      errorCode: observable,
      request: mobxAction.bound,
      render: mobxAction.bound,
    });
  }

  request(
    errorCode: string,
    context?: keyof CaptchaStatus,
    onSolved?: () => void,
  ) {
    this.errorCode = errorCode;
    this.context = context;
    this.onSolved = onSolved;
  }

  render() {
    if (!this.errorCode) {
      return;
    }
    this.onRendered(
      this.getCaptchaStore().renderForRefusedRequest(
        this.errorCode,
        this.onSolved,
      ),
      this.context,
    );
    this.errorCode = '';
    this.context = undefined;
    this.onSolved = undefined;
  }
}
